import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import LookupSuggest, { MetadataCard } from './LookupSuggest';
import SearchableSelect from './SearchableSelect';
import EmployeeSuggest from './EmployeeSuggest';
import { Check, X, Circle, RotateCcw, XCircle, CheckCircle, Reply, Trash2, Mail, UserCheck } from 'lucide-react';
import { formatDateTime, formatLongDate, getServerTime } from '../utils/datetime';

export default function RequestDetail({ request, user, onUpdate, isAdmin, isInitiator, isApprover, onClose }) {
  const [comment, setComment] = useState('');
  const [recipientId, setRecipientId] = useState('');
  const [parentCommentId, setParentCommentId] = useState(null);
  const [comments, setComments] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [flowVisualization, setFlowVisualization] = useState(null);
  const [fieldLabels, setFieldLabels] = useState({});
  const [formFieldDefs, setFormFieldDefs] = useState([]);
  const [stageUpdates, setStageUpdates] = useState({});
  const [savingData, setSavingData] = useState(false);
  const [masterStatus, setMasterStatus] = useState(request.status || 'pending');
  const [masterNodeId, setMasterNodeId] = useState(request.currentNodeId || '');
  const [masterFormName, setMasterFormName] = useState(request.formName || '');
  const [masterFormDataText, setMasterFormDataText] = useState(request.formData || '{}');
  const [masterFormValues, setMasterFormValues] = useState({});
  const [masterRawMode, setMasterRawMode] = useState(false);
  const [savingMaster, setSavingMaster] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(false);
  const [resendNodeId, setResendNodeId] = useState(request.currentNodeId || '');
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [parkEmployee, setParkEmployee] = useState(null);
  const [parkNotify, setParkNotify] = useState(true);
  const [parking, setParking] = useState(false);
  const [parkResult, setParkResult] = useState(null);
  const [stageClosedMessage, setStageClosedMessage] = useState(null);
  const [editState, setEditState] = useState({ canEdit: false, reason: '', editableFieldIds: [] });
  const [editOpen, setEditOpen] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [deptMembers, setDeptMembers] = useState([]);

  useEffect(() => {
    loadComments();
    loadApprovalHistory();
    loadFlowVisualization();
    loadFormFields();
    loadParticipants();
  }, [request.id]);

  useEffect(() => {
    setMasterStatus(request.status || 'pending');
    setMasterNodeId(request.currentNodeId || '');
    setMasterFormName(request.formName || '');
    setMasterFormDataText(request.formData || '{}');
    try {
      setMasterFormValues(JSON.parse(request.formData || '{}'));
    } catch (e) {
      setMasterFormValues({});
      setMasterRawMode(true);
    }
    setResendNodeId(request.currentNodeId || '');
    setResendResult(null);
    setParkEmployee(null);
    setParkResult(null);
    setStageClosedMessage(null);
  }, [request.id, request.status, request.currentNodeId, request.formName, request.formData]);

  const loadEditState = async () => {
    if (!isInitiator || request.status !== 'pending') {
      setEditState({ canEdit: false, reason: '', editableFieldIds: [] });
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/edit-state`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEditState({ canEdit: !!data.canEdit, reason: data.reason || '', editableFieldIds: data.editableFieldIds || [] });
        if (!data.canEdit) setEditOpen(false);
      }
    } catch (error) {
      console.error('Failed to load edit state:', error);
    }
  };

  useEffect(() => {
    setEditOpen(false);
    setEditError('');
    loadEditState();
  }, [request.id, request.status, isInitiator]);

  const [participants, setParticipants] = useState([]);

  const loadParticipants = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/participants`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setParticipants(data);
      }
    } catch (error) {
      console.error('Failed to load participants:', error);
    }
  };

  const loadComments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/comments`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const loadApprovalHistory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/history`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        let data = await response.json();
        
        const unique = [];
        const seen = new Set();
        data.forEach(h => {
          const key = `${h.action}|${h.approverName}|${h.nodeId}`;
          unique.push(h);
        });

        //remove "assignment_cancelled" from history
        const filtered = unique.filter(h => h.action !== 'assignment_cancelled');
        
        
        const grouped = [];
        for (const h of filtered) {
          if (grouped.length > 0) {
            const prev = grouped[grouped.length - 1];
            if (h.action === 'assigned' && prev.action === 'assigned') {
              const tPrev = getServerTime(prev.createdAt);
              const tCurr = getServerTime(h.createdAt);
              
              if (!isNaN(tPrev) && !isNaN(tCurr) && Math.abs(tCurr - tPrev) <= 2000) {
                prev.approverName = `${prev.approverName} / ${h.approverName}`;
                prev.nodeId = prev.nodeId ? `${prev.nodeId}/${h.nodeId}` : h.nodeId;
                continue;
              }
            }
          }
          grouped.push({ ...h });
        }

        setApprovalHistory(grouped);
      }
    } catch (error) {
      console.error('Failed to load approval history:', error);
    }
  };

  const loadFlowVisualization = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/flow`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFlowVisualization(data);
      }
    } catch (error) {
      console.error('Failed to load flow visualization:', error);
    }
  };

  const loadFormFields = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/form-fields`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const formFields = await response.json();
        const labels = {};
        formFields.forEach(field => {
          labels[field.id] = field.label;
        });
        setFieldLabels(labels);
        setFormFieldDefs(formFields || []);
      } else {
        setFieldLabels({});
        setFormFieldDefs([]);
      }
    } catch (error) {
      console.error('Failed to load form fields:', error);
      setFieldLabels({});
      setFormFieldDefs([]);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ comment: comment.trim(), recipientId: recipientId ? Number(recipientId) : null, parentCommentId })
      });

      if (response.ok) {
        setComment('');
        setRecipientId('');
        setParentCommentId(null);
        loadComments();
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const [approveTargetNodeId, setApproveTargetNodeId] = useState('');

  const currentFlow = (() => {
    try { return JSON.parse(request.flow || '{}'); } catch(e) { return {}; }
  })();
  const currentFlowNode = currentFlow.nodes && currentFlow.nodes.find(n => n.id === request.currentNodeId);
  const approvalNodeId = approveTargetNodeId || (request.approvableNodeIds || request.myOpenNodeIds || [])[0] || request.currentNodeId;
  const approvalFlowNode = currentFlow.nodes && currentFlow.nodes.find(n => n.id === approvalNodeId);
  const stageEditableFields = (approvalFlowNode && approvalFlowNode.data && approvalFlowNode.data.editableFields) || [];

  const currentNodeAssignee = (() => {
    if (!currentFlowNode || !currentFlowNode.data) return null;
    const type = currentFlowNode.data.type || 'employee';
    if (type === 'employee') {
      const a = currentFlowNode.data.assignee;
      if (!a) return null;
      if (Array.isArray(a)) return a.map(x => x.EmployeeName || x.Empcode).join(', ');
      return a.EmployeeName || a.Empcode || null;
    } else if (type === 'department') {
      return currentFlowNode.data.department ? `${currentFlowNode.data.department} (Dept.)` : null;
    } else if (type === 'general') {
      return 'Anyone';
    } else if (type === 'hod') {
      return "Creator's HOD";
    } else if (type === 'hod_reportee') {
      return "Reportees to Creator's HOD";
    }
    return null;
  })();

  const saveStageData = async () => {
    if (Object.keys(stageUpdates).length === 0) return true;
    setSavingData(true);
    try {
      const uploadedUpdates = { ...stageUpdates };
      for (const fieldId of Object.keys(uploadedUpdates)) {
        if (uploadedUpdates[fieldId] instanceof File) {
          const fd = new FormData();
          fd.append('image', uploadedUpdates[fieldId]);
          const upRes = await fetch(`${API_BASE_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: fd
          });
          if (!upRes.ok) { alert('Image upload failed'); setSavingData(false); return false; }
          const { url } = await upRes.json();
          uploadedUpdates[fieldId] = url;
        }
      }
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/update-data`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ fieldUpdates: uploadedUpdates, nodeId: approvalNodeId })
      });
      if (response.ok) {
        setStageUpdates({});
        onUpdate();
        //update the local request data to reflect changes without refetching
        request.formData = JSON.stringify({ ...JSON.parse(request.formData || '{}'), ...uploadedUpdates });
        return true;
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to save data');
        return false;
      }
    } catch (error) {
      console.error('Failed to save stage data:', error);
      alert('Failed to save data. Please try again.');
      return false;
    } finally {
      setSavingData(false);
    }
  };

  const getMissingRequiredStageFields = () => {
    return stageEditableFields
      .map(fieldId => formFieldDefs.find(f => f.id === fieldId))
      .filter(fieldDef => fieldDef && fieldDef.type !== 'section' && fieldDef.required)
      .filter(fieldDef => {
        const value = stageUpdates[fieldDef.id] !== undefined ? stageUpdates[fieldDef.id] : formData[fieldDef.id];
        return value === undefined || value === null ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0);
      })
      .map(fieldDef => fieldDef.label || fieldDef.id);
  };

  const handleApprove = async () => {
    const missingRequired = getMissingRequiredStageFields();
    if (missingRequired.length > 0) {
      alert(`Please fill in the required field(s) before approving: ${missingRequired.join(', ')}`);
      return;
    }

    if (!window.confirm('Are you sure you want to approve this request?')) return;

    setSubmitting(true);
    try {
      if (Object.keys(stageUpdates).length > 0) {
        const saved = await saveStageData();
        if (!saved) { setSubmitting(false); return; }
      }

      const body = { comment: comment.trim() };

      if (approvalNodeId) body.nodeId = approvalNodeId;

      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setComment('');
        setApproveTargetNodeId('');
        onUpdate();
        if (onClose) onClose();
        loadApprovalHistory();
        loadFlowVisualization();
        window.location.reload();
      } else {
        const data = await response.json();
        if (response.status === 409 && data.stageClosed) {
          setStageClosedMessage(data.message);
          onUpdate();
          loadApprovalHistory();
          loadFlowVisualization();
        } else {
          alert(data.message || 'Failed to approve request');
        }
      }
    } catch (error) {
      console.error('Failed to approve request:', error);
      alert('Failed to approve request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    const confirmMessage = comment.trim() 
      ? 'Are you sure you want to reject this request?' 
      : 'Are you sure you want to reject this request without a comment?';
    
    if (!window.confirm(confirmMessage)) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ comment: comment.trim() })
      });

      if (response.ok) {
        setComment('');
        onUpdate();
        if (onClose) onClose();
        loadApprovalHistory();
        loadFlowVisualization();
        window.location.reload();
      } else {
        const data = await response.json();
        if (response.status === 409 && data.stageClosed) {
          setStageClosedMessage(data.message);
          onUpdate();
          loadApprovalHistory();
          loadFlowVisualization();
        } else {
          alert(data.message || 'Failed to reject request');
        }
      }
    } catch (error) {
      console.error('Failed to reject request:', error);
      alert('Failed to reject request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canApprove = isApprover && request.status === 'pending' && request.isAwaitingMyApproval && !stageClosedMessage;

  const stageNotice = (() => {
    if (canApprove) return null;
    if (!isApprover && !request.myStageState && !stageClosedMessage) return null;

    if (stageClosedMessage) {
      return { tone: 'warning', title: 'This step was just closed', detail: stageClosedMessage };
    }

    const stageState = request.myStageState;
    const stamp = (value) => (value ? formatDateTime(value) : '');
    const movedOn = request.status === 'pending' && currentNodeAssignee
      ? ` It is now with ${currentNodeAssignee}.`
      : '';

    if (stageState) {
      const label = stageState.nodeLabel || 'this step';
      const when = stamp(stageState.actedAt);

      if (stageState.state === 'approved_by_me') {
        return {
          tone: 'done',
          title: 'You have already approved this step',
          detail: `You approved "${label}"${when ? ` on ${when}` : ''}. No further action is needed from you.${movedOn}`
        };
      }
      if (stageState.state === 'rejected_by_me') {
        return {
          tone: 'stopped',
          title: 'You rejected this request',
          detail: `You rejected "${label}"${when ? ` on ${when}` : ''}.`
        };
      }
      if (stageState.state === 'approved_by_other') {
        return {
          tone: 'done',
          title: 'This step has already been approved',
          detail: `${stageState.actorName} approved "${label}"${when ? ` on ${when}` : ''}, so nothing is needed from you.${movedOn}`
        };
      }
      if (stageState.state === 'rejected_by_other') {
        return {
          tone: 'stopped',
          title: 'This step has already been rejected',
          detail: `${stageState.actorName} rejected "${label}"${when ? ` on ${when}` : ''}, so nothing is needed from you.`
        };
      }
      if (stageState.state === 'reassigned') {
        return {
          tone: 'warning',
          title: 'This step is no longer assigned to you',
          detail: `"${label}" was reassigned${when ? ` on ${when}` : ''}.${stageState.note ? ` ${stageState.note}.` : ''}${movedOn}`
        };
      }
    }

    if (request.status === 'approved' || request.status === 'completed') {
      return { tone: 'done', title: 'This request is fully approved', detail: 'The approval flow has finished, so there is nothing left to action.' };
    }
    if (request.status === 'rejected') {
      return { tone: 'stopped', title: 'This request was rejected', detail: 'The approval flow ended here, so there is nothing left to action.' };
    }
    if (request.status === 'pending') {
      return {
        tone: 'info',
        title: 'This request has moved past your step',
        detail: `Nothing is waiting on you right now.${movedOn || ' It is with a later step in the flow.'}`
      };
    }

    return null;
  })();

  const stageNoticeStyles = {
    done: { background: '#f0fdf4', border: '#bbf7d0', heading: '#166534', body: '#15803d', Icon: CheckCircle },
    stopped: { background: '#fef2f2', border: '#fecaca', heading: '#991b1b', body: '#b91c1c', Icon: XCircle },
    warning: { background: '#fffbeb', border: '#fde68a', heading: '#92400e', body: '#b45309', Icon: RotateCcw },
    info: { background: '#f8fafc', border: '#e2e8f0', heading: '#334155', body: '#475569', Icon: Circle }
  };

  const renderStageNotice = () => {
    if (!stageNotice) return null;
    const theme = stageNoticeStyles[stageNotice.tone] || stageNoticeStyles.info;
    const { Icon } = theme;
    return (
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        padding: '14px 16px',
        marginBottom: '24px',
        background: theme.background,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px'
      }}>
        <Icon size={20} style={{ color: theme.heading, flexShrink: 0, marginTop: '1px' }} />
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: theme.heading }}>{stageNotice.title}</div>
          <div style={{ fontSize: '13px', color: theme.body, marginTop: '4px' }}>{stageNotice.detail}</div>
        </div>
      </div>
    );
  };

  const formData = JSON.parse(request.formData || '{}');
  const flowNodes = (currentFlow?.nodes || []).filter((node) => node.type !== 'start' && node.type !== 'end');

  // Splits the stored form data into fields this form actually defines (rendered in form order,
  // with labels and proper inputs) and leftover keys that no longer map to a field.
  const masterFieldGroups = (() => {
    const defined = (formFieldDefs || []).filter(field => field && field.id);
    const definedIds = new Set(defined.map(field => field.id));
    const extra = Object.keys(masterFormValues || {})
      .filter(key => key !== '__lookupMetadata' && !definedIds.has(key));
    return { defined, extra };
  })();

  const setMasterFieldValue = (fieldId, value) => {
    setMasterFormValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const toggleMasterRawMode = () => {
    if (masterRawMode) {
      let parsed;
      try {
        parsed = JSON.parse(masterFormDataText || '{}');
      } catch (error) {
        alert('Form data must be valid JSON before switching back to the detailed view');
        return;
      }
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        alert('Form data must be a JSON object');
        return;
      }
      setMasterFormValues(parsed);
      setMasterRawMode(false);
    } else {
      setMasterFormDataText(JSON.stringify(masterFormValues, null, 2));
      setMasterRawMode(true);
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('image', file);
    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      if (!response.ok) { alert('Image upload failed'); return null; }
      const { url } = await response.json();
      return url;
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Image upload failed');
      return null;
    }
  };

  const uploadMasterImage = async (fieldId, file) => {
    const url = await uploadImage(file);
    if (url) setMasterFieldValue(fieldId, url);
  };

  const masterFieldCtx = {
    prefix: 'master',
    setValue: setMasterFieldValue,
    setValues: setMasterFormValues,
    upload: uploadMasterImage
  };

  const setEditFieldValue = (fieldId, value) => {
    setEditValues(prev => ({ ...prev, [fieldId]: value }));
    setEditError('');
  };

  const uploadEditImage = async (fieldId, file) => {
    const url = await uploadImage(file);
    if (url) setEditFieldValue(fieldId, url);
  };

  const editFieldCtx = {
    prefix: 'edit',
    setValue: setEditFieldValue,
    setValues: setEditValues,
    upload: uploadEditImage,
    deptMembers
  };

  const editableFieldDefs = (formFieldDefs || []).filter(
    field => field && field.id && (field.type === 'section' || editState.editableFieldIds.includes(field.id))
  );

  const needsDeptMembers = editOpen && editableFieldDefs.some(field => field.type === 'department_members');

  useEffect(() => {
    if (!needsDeptMembers) return;
    fetch(`${API_BASE_URL}/api/department-members`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject('Failed to load members'))
      .then(setDeptMembers)
      .catch(err => {
        console.error('could not fetch department members', err);
        setDeptMembers([]);
      });
  }, [needsDeptMembers]);

  const openEdit = () => {
    try {
      setEditValues(JSON.parse(request.formData || '{}'));
    } catch (e) {
      setEditValues({});
    }
    setEditError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditError('');
  };

  const handleInitiatorSave = async () => {
    const isEmpty = (value) => value === undefined || value === null
      || (typeof value === 'string' && value.trim() === '')
      || (Array.isArray(value) && value.length === 0);
    const missing = editableFieldDefs
      .filter(field => field.type !== 'section' && field.required && isEmpty(editValues[field.id]))
      .map(field => field.label || field.id);
    if (missing.length > 0) {
      setEditError(`Please fill in required field(s): ${missing.join(', ')}`);
      return;
    }

    setSavingEdit(true);
    setEditError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/initiator-edit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ formData: editValues })
      });
      const data = await response.json();
      if (response.ok) {
        if (!data.unchanged) {
          request.formData = JSON.stringify(data.formData);
          request.status = data.status || request.status;
          request.currentNodeId = data.currentNodeId;
          request.currentNodeLabel = data.currentNodeLabel;
          onUpdate();
          loadApprovalHistory();
          loadFlowVisualization();
          loadEditState();
        }
        setEditOpen(false);
      } else {
        setEditError(data.message || 'Failed to update request');
        if (data.locked) {
          setEditState({ canEdit: false, reason: data.message || '', editableFieldIds: [] });
          setEditOpen(false);
        }
      }
    } catch (error) {
      console.error('Failed to update request:', error);
      setEditError('Failed to update request. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const sendAdminNotification = async (kind) => {
    const endpoint = kind === 'completion' ? 'admin-resend-completion' : 'admin-resend-notification';
    const body = kind === 'completion' ? {} : { nodeId: resendNodeId || undefined };

    setResending(kind);
    setResendResult(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (response.ok) {
        setResendResult({ ok: data.sent > 0, message: data.message, details: data.details || [] });
        loadApprovalHistory();
        loadFlowVisualization();
      } else {
        setResendResult({ ok: false, message: data.message || 'Failed to send notification', details: [] });
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      setResendResult({ ok: false, message: 'Failed to send notification. Please try again.', details: [] });
    } finally {
      setResending(false);
    }
  };

  const handleAdminPark = async () => {
    const empCode = parkEmployee?.Empcode || parkEmployee?.EmpCode;
    if (!empCode) {
      setParkResult({ ok: false, message: 'Search and select an employee first' });
      return;
    }

    const stageLabel = resendNodeId
      ? (flowNodes.find(n => n.id === resendNodeId)?.data?.label || resendNodeId)
      : (request.currentNodeLabel || 'the current stage');

    if (!window.confirm(`Park this request at "${stageLabel}" with ${parkEmployee.EmployeeName} (${empCode})?\n\nThey become the only approver for that stage and anyone previously assigned there loses access.`)) {
      return;
    }

    setParking(true);
    setParkResult(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/admin-park`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ empCode, nodeId: resendNodeId || undefined, notify: parkNotify })
      });

      const data = await response.json();
      if (response.ok) {
        setParkResult({
          ok: true,
          message: data.message,
          details: data.details || [],
          hasAccount: data.parkedWith?.hasAccount,
          cancelled: data.replaced?.cancelled || 0
        });
        onUpdate();
        loadApprovalHistory();
        loadFlowVisualization();
      } else {
        setParkResult({ ok: false, message: data.message || 'Failed to park request' });
      }
    } catch (error) {
      console.error('Failed to park request:', error);
      setParkResult({ ok: false, message: 'Failed to park request. Please try again.' });
    } finally {
      setParking(false);
    }
  };

  const handleAdminMasterSave = async () => {
    let parsedFormData = {};
    if (masterRawMode) {
      try {
        parsedFormData = JSON.parse(masterFormDataText || '{}');
      } catch (error) {
        alert('Form data must be valid JSON');
        return;
      }
    } else {
      parsedFormData = masterFormValues;
    }

    setSavingMaster(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}/admin-master-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: masterStatus,
          currentNodeId: masterStatus === 'pending' ? masterNodeId : null,
          formName: masterFormName,
          formData: parsedFormData
        })
      });

      if (response.ok) {
        onUpdate();
        loadApprovalHistory();
        loadFlowVisualization();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to update request');
      }
    } catch (error) {
      console.error('Failed to update request:', error);
      alert('Failed to update request. Please try again.');
    } finally {
      setSavingMaster(false);
    }
  };

  const handleAdminDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    setDeletingRequest(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/requests/${request.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        onUpdate();
        if (onClose) onClose();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to delete request');
      }
    } catch (error) {
      console.error('Failed to delete request:', error);
      alert('Failed to delete request. Please try again.');
    } finally {
      setDeletingRequest(false);
    }
  };

  const SIMPLE_INPUT_TYPES = {
    date: 'date',
    time: 'time',
    datetime: 'datetime-local',
    number: 'number',
    email: 'email',
    text: 'text'
  };

  // Renders one editable field for the admin master control, driven by the form's own
  // field definition so admins see labels and proper inputs instead of raw JSON.
  const renderMasterField = (fieldDef, rawValue, ctx = masterFieldCtx) => {
    const fieldId = fieldDef.id;
    const value = rawValue === undefined || rawValue === null ? '' : rawValue;
    const lookupCfg = fieldDef.lookup && fieldDef.lookup.datasetId ? fieldDef.lookup : null;

    if (lookupCfg) {
      return (
        <LookupSuggest
          datasetId={lookupCfg.datasetId}
          mode={lookupCfg.mode || 'warn'}
          value={typeof value === 'string' ? value : ''}
          placeholder={fieldDef.placeholder || ''}
          onChangeText={(v) => ctx.setValue(fieldId, v)}
          onSelect={(item) => {
            if ((lookupCfg.mode || 'warn') === 'select') {
              ctx.setValues(prev => ({
                ...prev,
                [fieldId]: item.value,
                __lookupMetadata: { ...(prev.__lookupMetadata || {}), [fieldId]: { label: item.label, metadata: item.metadata, value: item.value } }
              }));
            }
          }}
        />
      );
    }

    if (fieldDef.type === 'image') {
      return (
        <div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) ctx.upload(fieldId, file);
            }}
            style={{ display: 'block', marginBottom: 6 }}
          />
          {value ? (
            <div>
              <img
                src={String(value).startsWith('/uploads') ? `${API_BASE_URL}${value}` : value}
                alt={fieldDef.label || fieldId}
                style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 4, border: '1px solid #e2e8f0', display: 'block', marginBottom: 4 }}
              />
              <button type="button" className="button-muted" onClick={() => ctx.setValue(fieldId, '')}>Clear image</button>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#64748b' }}>No image</div>
          )}
        </div>
      );
    }

    if (fieldDef.type === 'textarea') {
      return (
        <textarea
          rows={3}
          className="form-field-textarea"
          maxLength={fieldDef.maxLength || undefined}
          value={typeof value === 'string' ? value : String(value)}
          onChange={(e) => ctx.setValue(fieldId, e.target.value)}
        />
      );
    }

    if (fieldDef.type === 'select') {
      const options = fieldDef.options || [];
      const isKnown = options.includes(value);
      return (
        <SearchableSelect
          className="form-field-input"
          value={value == null ? '' : String(value)}
          onChange={(v) => ctx.setValue(fieldId, v)}
          options={isKnown || value === '' || value == null ? options : [...options, String(value)]}
        />
      );
    }

    if (fieldDef.type === 'multiselect') {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="field-checkbox-group">
          {(fieldDef.options || []).map((opt, idx) => (
            <div key={idx} className="field-checkbox">
              <input
                type="checkbox"
                id={`${ctx.prefix}-${fieldId}-${idx}`}
                checked={selected.includes(opt)}
                onChange={(e) => {
                  const next = [...selected];
                  if (e.target.checked) next.push(opt);
                  else {
                    const i = next.indexOf(opt);
                    if (i !== -1) next.splice(i, 1);
                  }
                  ctx.setValue(fieldId, next);
                }}
              />
              <label htmlFor={`${ctx.prefix}-${fieldId}-${idx}`}>{opt}</label>
            </div>
          ))}
        </div>
      );
    }

    if (fieldDef.type === 'department_members' && ctx.deptMembers) {
      const selected = Array.isArray(value) ? value : [];
      if (ctx.deptMembers.length === 0) return <div>Loading department members...</div>;
      return (
        <div>
          {ctx.deptMembers.map(m => {
            const checked = selected.some(s => (typeof s === 'object' ? s.code : s) === m.Empcode);
            return (
              <div key={m.Empcode} className="field-checkbox">
                <input
                  type="checkbox"
                  id={`${ctx.prefix}-${fieldId}-${m.Empcode}`}
                  checked={checked}
                  onChange={(e) => {
                    const next = [...selected];
                    if (e.target.checked) next.push({ code: m.Empcode, name: m.EmployeeName });
                    else {
                      const i = next.findIndex(s => (typeof s === 'object' ? s.code : s) === m.Empcode);
                      if (i !== -1) next.splice(i, 1);
                    }
                    ctx.setValue(fieldId, next);
                  }}
                />
                <label htmlFor={`${ctx.prefix}-${fieldId}-${m.Empcode}`}>{m.EmployeeName}</label>
              </div>
            );
          })}
        </div>
      );
    }

    // Complex values (department members, lookup arrays) stay editable as JSON for that one field.
    if (typeof value === 'object') {
      return (
        <textarea
          rows={3}
          className="form-field-textarea"
          value={JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try {
              ctx.setValue(fieldId, JSON.parse(e.target.value || 'null'));
            } catch (err) {
              // keep the last valid value until the admin finishes typing valid JSON
            }
          }}
        />
      );
    }

    return (
      <input
        type={SIMPLE_INPUT_TYPES[fieldDef.type] || 'text'}
        className="form-field-input"
        value={value}
        placeholder={fieldDef.placeholder || ''}
        maxLength={fieldDef.maxLength || undefined}
        onChange={(e) => ctx.setValue(fieldId, e.target.value)}
      />
    );
  };

  const buildCommentTree = (list) => {
    const map = {};
    list.forEach(c => { map[c.id] = { ...c, children: [] }; });
    const roots = [];
    list.forEach(c => {
      if (c.parentCommentId) {
        const parent = map[c.parentCommentId];
        if (parent) parent.children.push(map[c.id]);
        else roots.push(map[c.id]);
      } else {
        roots.push(map[c.id]);
      }
    });
    const sortFn = (a, b) => getServerTime(a.createdAt) - getServerTime(b.createdAt);
    const sortRecursive = (nodes) => {
      nodes.sort(sortFn);
      nodes.forEach(n => n.children && sortRecursive(n.children));
    };
    sortRecursive(roots);
    return roots;
  };

  const renderComment = (c, depth = 0) => {
    return (
      <div
        key={c.id}
        style={{
          marginLeft: depth * 20,
          padding: '12px',
          background: c.recipientId ? '#fff7ed' : '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontWeight: '600', color: '#0f172a' }}>
            {c.userName} {c.recipientId ? <span style={{ fontSize: '12px', color: '#92400e', marginLeft: '8px' }}>(private--to--{c.recipientName})</span> : null}
          </span>
          <span style={{ fontSize: '12px', color: '#64748b' }}>
            {formatDateTime(c.createdAt)}
          </span>
        </div>
        <div style={{ fontSize: '14px', color: '#475569', marginBottom: '8px' }}>
          {c.comment}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <p
          onClick={() => { 
          setParentCommentId(c.id); setComment('@' + c.userName + ' '); setRecipientId(c.userId); }} 
          className="button-link"
          style={{ fontSize: '12px', padding: '4px 8px', cursor: 'pointer', color: '#3b82f6' }}
          >
          Reply
          <span style={{ marginLeft: '4px' }}><Reply size={12} /></span>
          
          </p>
        </div>
        {c.children && c.children.length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {c.children.map(child => renderComment(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="detail-header">
        <h3>{request.formName}</h3>
        <span className={`status-badge status-${request.status}`}>
          {request.status}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
        {renderStageNotice()}

        <div className="detail-meta">
          
          <div className="meta-item">
            <span className="meta-label">Id:</span>
            <span className="meta-value">{request.id}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Submitted By</span>
            <span className="meta-value">{request.initiatorName}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Date</span>
            <span className="meta-value">
              {formatLongDate(request.createdAt)}
            </span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Current Step</span>
            <span className="meta-value">
              {request.currentNodeLabel
                ? `${currentNodeAssignee}` : ''}
             
            </span>
           
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '12px' }}>
            Form Data
          </h4>
          <div className="detail-data">
            {Object.entries(formData).filter(([key]) => key !== '__lookupMetadata').map(([key, value]) => {
              const fieldDef = formFieldDefs.find(f => f.id === key);
              const isImage = fieldDef && fieldDef.type === 'image';
              const meta = formData.__lookupMetadata && formData.__lookupMetadata[key];
              return (
                <div key={key} className="detail-field">
                  <span className="detail-label">{fieldLabels[key] || key}</span>
                  {isImage && value ? (
                    <img
                      src={value.startsWith('/uploads') ? `${API_BASE_URL}${value}` : value}
                      alt={fieldLabels[key] || key}
                      style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, border: '1px solid #e2e8f0', marginTop: 4 }}
                    />
                  ) : (
                    <span className="detail-value">{Array.isArray(value) ? value.map(v => typeof v === 'object' && v.name ? v.name : v).join(', ') : value}</span>
                  )}
                  {meta && Array.isArray(meta.metadata) && meta.metadata.length > 0 && (
                    <MetadataCard items={meta.metadata} title={meta.label} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {isInitiator && editState.canEdit && !editOpen && (
          <div style={{ marginBottom: '24px', padding: '12px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', color: '#0c4a6e' }}>
              You can edit this request until the first approver acts on it.
            </span>
            <button type="button" className="button-outline" onClick={openEdit}>Edit request</button>
          </div>
        )}

        {isInitiator && editOpen && (
          <div style={{ marginBottom: '24px', padding: '16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0c4a6e', marginBottom: '4px' }}>
              Edit Request
            </h4>
            <div style={{ fontSize: '12px', color: '#0369a1', marginBottom: '12px' }}>
              Changes are recorded in the history and the current approver is notified.
            </div>
            {editableFieldDefs.map(fieldDef => {
              if (fieldDef.type === 'section') {
                return (
                  <div key={fieldDef.id} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                    <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{fieldDef.label || 'Section'}</span>
                    <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                  </div>
                );
              }
              return (
                <div key={fieldDef.id} style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0f172a', marginBottom: '4px' }}>
                    {fieldDef.label || fieldDef.id}
                    {fieldDef.required && <span style={{ color: '#dc2626' }}> *</span>}
                  </label>
                  {renderMasterField(fieldDef, editValues[fieldDef.id], editFieldCtx)}
                </div>
              );
            })}
            {editError && <div className="field-error" style={{ marginBottom: '12px' }}>{editError}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" className="button-outline" onClick={closeEdit} disabled={savingEdit}>Cancel</button>
              <button type="button" className="button-primary" onClick={handleInitiatorSave} disabled={savingEdit}>
                {savingEdit ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        )}

        {canApprove && stageEditableFields.length > 0 && (
          <div style={{ marginBottom: '24px', padding: '16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0c4a6e', marginBottom: '12px' }}>
              Update Data at This Stage
            </h4>
            {stageEditableFields.map(fieldId => {
              const fieldDef = formFieldDefs.find(f => f.id === fieldId);
              if (!fieldDef) return null;
              if (fieldDef.type === 'section') {
                return (
                  <div key={fieldId} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                    <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{fieldDef.label || 'Section'}</span>
                    <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                  </div>
                );
              }
              const currentVal = stageUpdates[fieldId] !== undefined ? stageUpdates[fieldId] : (formData[fieldId] || '');
              const lookupCfg = fieldDef.lookup && fieldDef.lookup.datasetId ? fieldDef.lookup : null;
              return (
                <div key={fieldId} style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0f172a', marginBottom: '4px' }}>
                    {fieldDef.label}
                    {fieldDef.required && <span style={{ color: '#dc2626' }}> *</span>}
                  </label>
                  {lookupCfg ? (
                    <LookupSuggest
                      datasetId={lookupCfg.datasetId}
                      mode={lookupCfg.mode || 'warn'}
                      value={currentVal}
                      placeholder={fieldDef.placeholder || ''}
                      onChangeText={(v) => setStageUpdates(prev => ({ ...prev, [fieldId]: v }))}
                      onSelect={(item) => {
                        if ((lookupCfg.mode || 'warn') === 'select') {
                          setStageUpdates(prev => ({
                            ...prev,
                            [fieldId]: item.value,
                            __lookupMetadata: { ...(prev.__lookupMetadata || formData.__lookupMetadata || {}), [fieldId]: { label: item.label, metadata: item.metadata, value: item.value } }
                          }));
                        }
                      }}
                    />
                  ) : fieldDef.type === 'image' ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files && e.target.files[0];
                          if (file) setStageUpdates(prev => ({ ...prev, [fieldId]: file }));
                        }}
                        style={{ display: 'block', marginBottom: 6 }}
                      />
                      {stageUpdates[fieldId] instanceof File && (
                        <div style={{ fontSize: 12, color: '#0369a1' }}>{stageUpdates[fieldId].name} selected</div>
                      )}
                      {!(stageUpdates[fieldId] instanceof File) && formData[fieldId] && (
                        <img
                          src={formData[fieldId].startsWith('/uploads') ? `${API_BASE_URL}${formData[fieldId]}` : formData[fieldId]}
                          alt={fieldDef.label}
                          style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 4, border: '1px solid #e2e8f0' }}
                        />
                      )}
                    </div>
                  ) : fieldDef.type === 'textarea' ? (
                    <textarea
                      value={currentVal}
                      onChange={(e) => setStageUpdates(prev => ({ ...prev, [fieldId]: e.target.value }))}
                      rows={3}
                      maxLength={fieldDef.maxLength || undefined}
                      className="form-field-textarea"
                    />
                  ) : fieldDef.type === 'select' ? (
                    <SearchableSelect
                      value={currentVal == null ? '' : String(currentVal)}
                      onChange={(v) => setStageUpdates(prev => ({ ...prev, [fieldId]: v }))}
                      options={fieldDef.options || []}
                      className="form-field-input"
                    />
                  ) : fieldDef.type === 'multiselect' ? (
                    <div className="field-checkbox-group">
                      {(fieldDef.options || []).map((opt, idx) => {
                        const selected = Array.isArray(currentVal) ? currentVal : [];
                        const checked = selected.includes(opt);
                        return (
                          <div key={idx} className="field-checkbox">
                            <input
                              type="checkbox"
                              id={`stage-${fieldId}-${idx}`}
                              checked={checked}
                              onChange={(e) => {
                                const prev = Array.isArray(currentVal) ? [...currentVal] : [];
                                if (e.target.checked) prev.push(opt);
                                else {
                                  const i = prev.indexOf(opt);
                                  if (i !== -1) prev.splice(i, 1);
                                }
                                setStageUpdates(prevState => ({ ...prevState, [fieldId]: prev }));
                              }}
                            />
                            <label htmlFor={`stage-${fieldId}-${idx}`}>{opt}</label>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <input
                      type={fieldDef.type === 'date' ? 'date' : fieldDef.type === 'time' ? 'time' : fieldDef.type === 'number' ? 'number' : fieldDef.type === 'email' ? 'email' : 'text'}
                      value={currentVal}
                      onChange={(e) => setStageUpdates(prev => ({ ...prev, [fieldId]: e.target.value }))}
                      placeholder={fieldDef.placeholder || ''}
                      maxLength={fieldDef.maxLength || undefined}
                      className="form-field-input"
                    />
                  )}
                  {fieldDef.maxLength > 0 && ['text', 'textarea', 'email'].includes(fieldDef.type) && (
                    <div style={{ marginTop: 4, fontSize: 12, textAlign: 'right', color: String(currentVal || '').length >= fieldDef.maxLength ? '#dc2626' : '#94a3b8' }}>
                      {String(currentVal || '').length} / {fieldDef.maxLength}
                    </div>
                  )}
                </div>
              );
            })}
            <button
              onClick={saveStageData}
              disabled={savingData || Object.keys(stageUpdates).length === 0}
              className="button-primary"
              style={{ marginTop: '4px' }}
            >
              {savingData ? 'Saving...' : 'Save Updates'}
            </button>
          </div>
        )}

        {flowVisualization && (
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '12px' }}>
              Approval Flow Progress
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {flowVisualization.nodes.map((node, idx) => (
                <div
                  key={node.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: node.status === 'current' ? '#fef3c7' :
                               node.status === 'completed' ? '#dcfce7' :
                               node.status === 'rejected' ? '#fee2e2' :
                               node.status === 'reverted' ? '#f3e8ff' :
                               node.status === 'partial' ? '#fff7ed' :
                               node.status === 'assigned' ? '#eef2ff' :
                               node.status === 'skipped' ? '#f1f5f9' :
                               '#f8fafc',
                    border: `1px solid ${
                      node.status === 'current' ? '#fbbf24' :
                      node.status === 'completed' ? '#86efac' :
                      node.status === 'rejected' ? '#ef4444' :
                      node.status === 'reverted' ? '#7c3aed' :
                      node.status === 'partial' ? '#f59e0b' :
                      node.status === 'assigned' ? '#60a5fa' :
                      '#e2e8f0'
                    }`,
                    borderRadius: '8px'
                  }}
                >
                  <div style={{
                    fontSize: '24px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f4f5f7',
                    borderRadius: '50%'
                  }}>
                    {node.status === 'completed' ? <Check size={20} color="green" /> :
                     node.status === 'rejected' ? <X size={20} color="red" /> :
                     node.status === 'reverted' ? <RotateCcw size={20} color="#7c3aed" /> :
                     node.status === 'partial' ? <Circle size={20} color="#f59e0b" /> :
                     node.status === 'assigned' ? <Circle size={20} color="#2563eb" /> :
                     <Circle size={20} /> }
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      {/* <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{node.label}</div> */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {node.grouped && node.childIds && (
                          <div style={{ fontSize: '12px', color: '#475569', background: '#eef2ff', padding: '2px 6px', borderRadius: '12px' }}>
                            {node.childIds.length} approvers
                          </div>
                        )}
                        {node.isAssignedToCurrentUser && (
                          <div style={{ fontSize: '12px', color: '#fff', background: '#06b6d4', padding: '2px 6px', borderRadius: '12px' }}>You</div>
                        )}
                      </div>
                    </div>
                    
                    {/* {Initiator Name for first Node} */}

                    {idx === 0 && request.initiatorName && (
                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>
                        Initiated by {request.initiatorName}
                      </div>
                    )}
                    
                    {node.assignee && idx!=0 && (
                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px' }}>
                        {node.assignee}
                      </div>
                    )}

                   
                    {((node.approverNames && node.approverNames.length > 1) || (node.requiredApprovals && node.requiredApprovals > 1) || (node.parentsCount && node.parentsCount > 1)) ? (
                      <div style={{ fontSize: '12px', color: '#92400e', marginTop: '6px' }}>
                        {node.approverNames && node.approverNames.length > 1 ? `Approved by ${node.approverNames.join(', ')}` : null}
                        {(!node.approverNames || node.approverNames.length === 0) && node.status === 'partial' ? `${node.approvalsCount}/${node.requiredApprovals} approvals` : null}
                      </div>
                    ): null

                  }

                    {node.status === 'rejected' && node.approverNames && node.approverNames.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '6px' }}>
                        Rejected by {node.approverNames.join(', ')}
                      </div>
                    )}
                    {node.status === 'reverted' && node.approverNames && node.approverNames.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#6d28d9', marginTop: '6px' }}>
                        Reverted by {node.approverNames.join(', ')}
                      </div>
                    )}
                    {node.status === 'completed' && node.approverNames && node.approverNames.length > 0 && !(node.approverNames && node.approverNames.length > 1) && (
                      <div style={{ fontSize: '12px', color: '#16a34a', marginTop: '6px' }}>
                        Approved by {node.approverNames.join(', ')}
                      </div>
                    )}
                    {node.status === 'current' && (
                      <div style={{ fontSize: '12px', color: '#d97706', marginTop: '6px' }}>
                        Awaiting approval
                      </div>
                    )}
                    {node.status === 'skipped' && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', fontStyle: 'italic' }}>
                        Skipped - not required for this request
                      </div>
                    )}
                  </div>
                  {node.actionTime && (
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {formatDateTime(node.actionTime)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div style={{ marginBottom: '24px', padding: '16px', background: '#eef6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e3a8a', marginBottom: '12px' }}>
              Request Master Control
            </h4>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: '#1e3a8a', marginBottom: '4px' }}>Form Name</label>
                <input
                  className="form-field-input"
                  value={masterFormName}
                  onChange={(e) => setMasterFormName(e.target.value)}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: '#1e3a8a', marginBottom: '4px' }}>Status</label>
                <select className="form-field-input" value={masterStatus} onChange={(e) => setMasterStatus(e.target.value)}>
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>
              {masterStatus === 'pending' && (
                <div>
                  <label style={{ display: 'block', fontSize: '13px', color: '#1e3a8a', marginBottom: '4px' }}>Current Stage</label>
                  <select className="form-field-input" value={masterNodeId} onChange={(e) => setMasterNodeId(e.target.value)}>
                    <option value="">Select stage</option>
                    {flowNodes.map((node) => (
                      <option key={node.id} value={node.id}>{node.data?.label || node.id}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a8a' }}>Form Data</label>
                  <button type="button" className="button-outline button-sm" onClick={toggleMasterRawMode}>
                    {masterRawMode ? 'Detailed view' : 'Raw JSON'}
                  </button>
                </div>

                {masterRawMode ? (
                  <textarea
                    rows={12}
                    className="form-field-textarea"
                    value={masterFormDataText}
                    onChange={(e) => setMasterFormDataText(e.target.value)}
                  />
                ) : (
                  <div style={{ background: '#fff', border: '1px solid #dbeafe', borderRadius: '6px', padding: '12px' }}>
                    {masterFieldGroups.defined.length === 0 && masterFieldGroups.extra.length === 0 && (
                      <div style={{ fontSize: '13px', color: '#64748b' }}>This request has no form data.</div>
                    )}

                    {masterFieldGroups.defined.map(fieldDef => {
                      if (fieldDef.type === 'section') {
                        return (
                          <div key={fieldDef.id} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0' }}>
                            <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{fieldDef.label || 'Section'}</span>
                            <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
                          </div>
                        );
                      }
                      return (
                        <div key={fieldDef.id} style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#0f172a', marginBottom: '4px' }}>
                            {fieldDef.label || fieldDef.id}
                            {fieldDef.required && <span style={{ color: '#dc2626' }}> *</span>}
                          </label>
                          {renderMasterField(fieldDef, masterFormValues[fieldDef.id])}
                        </div>
                      );
                    })}

                    {masterFieldGroups.extra.length > 0 && (
                      <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px dashed #cbd5e1' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: '8px' }}>
                          Values with no matching field in this form
                        </div>
                        {masterFieldGroups.extra.map(key => (
                          <div key={key} style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#0f172a', marginBottom: '4px' }}>
                              {fieldLabels[key] || key}
                            </label>
                            {renderMasterField({ id: key, type: 'text' }, masterFormValues[key])}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ paddingTop: '12px', borderTop: '1px solid #bfdbfe' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#1e3a8a', marginBottom: '4px' }}>
                  Target Stage
                </label>
                <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '8px' }}>
                  Used by the parking and notification actions below.
                </div>
                <select
                  className="form-field-input"
                  style={{ width: '280px' }}
                  value={resendNodeId}
                  onChange={(e) => setResendNodeId(e.target.value)}
                >
                  <option value="">Current stage{request.currentNodeLabel ? ` (${request.currentNodeLabel})` : ''}</option>
                  {flowNodes.map((node) => (
                    <option key={node.id} value={node.id}>{node.data?.label || node.id}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#1e3a8a', marginBottom: '4px' }}>
                  Park With Employee
                </label>
                <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '8px' }}>
                  Hand the target stage to any FTTS employee, searched by name or employee code.
                  They become that stage's only approver and anyone previously assigned there loses access.
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <EmployeeSuggest
                    value={parkEmployee ? parkEmployee.EmployeeName : ''}
                    onChange={(item) => { setParkEmployee(item); setParkResult(null); }}
                    placeholder="Name or employee code"
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#1e3a8a' }}>
                    <input type="checkbox" checked={parkNotify} onChange={(e) => setParkNotify(e.target.checked)} />
                    Email them
                  </label>
                  <button
                    className="button-outline"
                    onClick={handleAdminPark}
                    disabled={parking || !parkEmployee || savingMaster || deletingRequest || request.status !== 'pending'}
                    title={request.status !== 'pending' ? 'Only a pending request can be parked' : ''}
                  >
                    <UserCheck size={16} style={{ marginRight: '4px' }} />
                    {parking ? 'Parking...' : 'Park Request'}
                  </button>
                </div>
                {parkEmployee && (
                  <div style={{ fontSize: '12px', color: '#475569', marginTop: '6px' }}>
                    Selected: {parkEmployee.EmployeeName} ({parkEmployee.Empcode})
                    {parkEmployee.DepartmentName ? ` - ${parkEmployee.DepartmentName}` : ''}
                    {parkEmployee.Email ? ` - ${parkEmployee.Email}` : ''}
                  </div>
                )}
                {parkResult && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: parkResult.ok ? '#dcfce7' : '#fef3c7',
                    border: `1px solid ${parkResult.ok ? '#86efac' : '#fbbf24'}`,
                    color: parkResult.ok ? '#166534' : '#92400e'
                  }}>
                    <div style={{ fontWeight: 600 }}>{parkResult.message}</div>
                    {parkResult.cancelled > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        Removed {parkResult.cancelled} previous assignment(s) on this stage.
                      </div>
                    )}
                    {parkResult.hasAccount === false && (
                      <div style={{ marginTop: '4px' }}>
                        They had no approvals account, so one was created. Their first sign-in sets the password.
                      </div>
                    )}
                    {(parkResult.details || []).filter(d => !d.sent && d.reason).map((d, idx) => (
                      <div key={idx} style={{ marginTop: '4px' }}>
                        {d.recipient ? `${d.recipient}: ` : ''}{d.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#1e3a8a', marginBottom: '4px' }}>
                  Manual Notifications
                </label>
                <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '8px' }}>
                  Approver mail re-fires <code>request.assigned</code> for the target stage and records the assignment if missing.
                  Completion mail re-fires <code>request.completed</code> to the initiator once the flow has finished.
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    className="button-outline"
                    onClick={() => sendAdminNotification('assignment')}
                    disabled={!!resending || savingMaster || deletingRequest}
                  >
                    <Mail size={16} style={{ marginRight: '4px' }} />
                    {resending === 'assignment' ? 'Sending...' : 'Send Approver Email'}
                  </button>
                  <button
                    className="button-outline"
                    onClick={() => sendAdminNotification('completion')}
                    disabled={!!resending || savingMaster || deletingRequest || request.status !== 'approved'}
                    title={request.status !== 'approved' ? 'Available once the request is fully approved' : ''}
                  >
                    <CheckCircle size={16} style={{ marginRight: '4px' }} />
                    {resending === 'completion' ? 'Sending...' : 'Send Completion Email'}
                  </button>
                </div>
                {resendResult && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    background: resendResult.ok ? '#dcfce7' : '#fef3c7',
                    border: `1px solid ${resendResult.ok ? '#86efac' : '#fbbf24'}`,
                    color: resendResult.ok ? '#166534' : '#92400e'
                  }}>
                    <div style={{ fontWeight: 600 }}>{resendResult.message}</div>
                    {(resendResult.details || []).filter(d => !d.sent && d.reason).map((d, idx) => (
                      <div key={idx} style={{ marginTop: '4px' }}>
                        {d.recipient ? `${d.recipient}: ` : ''}{d.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="button-primary" onClick={handleAdminMasterSave} disabled={savingMaster || deletingRequest}>
                  {savingMaster ? 'Saving...' : 'Save Master Update'}
                </button>
                <button className="button-outline" style={{ color: '#dc2626', borderColor: '#dc2626' }} onClick={handleAdminDelete} disabled={savingMaster || deletingRequest}>
                  <Trash2 size={16} style={{ marginRight: '4px' }} /> {deletingRequest ? 'Deleting...' : 'Delete Request'}
                </button>
              </div>
            </div>
          </div>
        )}

        {approvalHistory.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '12px' }}>
              Approval History
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {approvalHistory.map((history, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div>
                      <span style={{ fontWeight: '600', color: '#0f172a' }}>
                        {history.approverName}
                      </span>
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: history.action === 'approved' ? '#dcfce7' :
                                   history.action === 'rejected' ? '#fee2e2' :
                                   history.action === 'reverted' ? '#f3e8ff' :
                                   history.action === 'edited' ? '#e0f2fe' : '#fef3c7',
                        color: history.action === 'approved' ? '#166534' :
                               history.action === 'rejected' ? '#991b1b' :
                               history.action === 'reverted' ? '#6d28d9' :
                               history.action === 'edited' ? '#075985' : '#92400e'
                      }}>
                        {history.action}
                      </span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                      {formatDateTime(history.createdAt)}
                    </span>
                  </div>
                  {history.comment && (
                    <div style={{ fontSize: '14px', color: '#475569', whiteSpace: 'pre-wrap' }}>
                      {history.comment}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '12px' }}>
            Comments
          </h4>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} className="form-field-input" style={{ width: '240px' }}>
                <option value="">Public (visible to all)</option>
                {participants.map(p => (
                  <option key={p.id} value={p.id}>{p.name} {p.code ? `(${p.code})` : ''}</option>
                ))}
              </select>

              {parentCommentId && (
                <div style={{ alignSelf: 'center', color: '#475569' }}>
                  Replying to comment #{parentCommentId} <button onClick={() => setParentCommentId(null)} style={{ marginLeft: '8px' }}>Cancel</button>
                </div>
              )}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              rows={3}
              className="form-field-textarea"
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={handleAddComment}
                className="button-primary"
                disabled={!comment.trim()}
              >
                Add Comment
              </button>
            </div>
          </div>

          {comments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {buildCommentTree(comments).map(root => renderComment(root, 0))}
            </div>
          )}
        </div>
      </div>

      {canApprove && (
        <div style={{
          borderTop: '1px solid #e2e8f0',
          paddingTop: '16px',
          display: 'flex',
          gap: '8px',
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleReject}
            className="button-outline approve-btn"
            disabled={submitting}
            style={{ color: '#dc2626', borderColor: '#dc2626' }}
          >
            <XCircle size={16} style={{ marginRight: '4px' }} /> Reject
          </button>
          <button
            onClick={handleApprove}
            className="button-primary approve-btn"
            disabled={submitting}
          >
            <CheckCircle size={16} style={{ marginRight: '4px' }} /> Approve
          </button>
        </div>
      )}
    </div>
  );
}
